/**
 * Feb 2026 — RM Repository edit payload builder.
 *
 * Prior behaviour: `handleSaveTags` sent the entire `tagForm` object,
 * which meant `dual_uom` was ALWAYS present (initialised from
 * `rm.dual_uom || false` in `handleEditTags`). Whenever the RM being
 * edited was not currently dual-UOM, the frontend shipped an explicit
 * `dual_uom: false` — and the backend's `update_raw_material` Case A
 * (`rm_routes.py:5642`) responds by force-nulling `procurement_uom`,
 * `consumption_uom`, and `conversion_factor`. Any latent UOM data on
 * the row (from bulk uploads / migrations / pre-dual data) was
 * silently wiped on every unrelated edit.
 *
 * Fix: narrow the outbound payload so that `dual_uom` (and the UOM
 * triple) are sent only when they're relevant to the user's intent:
 *
 *   • `tagForm.dual_uom === true`
 *        → user has dual on now; send `dual_uom: true` + triple.
 *          Backend Case C writes them (AC1, AC3).
 *
 *   • `tagForm.dual_uom === false` AND `editingRM.dual_uom === true`
 *        → user explicitly turned dual OFF from the ON state;
 *          send `dual_uom: false` alone. Backend Case A force-nulls
 *          the triple (AC2 — expected behaviour on explicit toggle).
 *
 *   • otherwise (user never touched dual, RM wasn't dual)
 *        → omit `dual_uom` and the triple entirely. Backend's
 *          `if any(k in data for k in (dual_uom, procurement_uom,
 *          consumption_uom, conversion_factor))` gate at
 *          `rm_routes.py:5639` short-circuits the whole UOM block.
 *          Latent UOM data on the doc is preserved.
 *
 * Pure function, no React dependencies — importable by Jest.
 *
 * NOTE: ESM exports — the production webpack build rejects named ESM
 * imports from CommonJS modules. Jest coverage runs via `craco test`
 * (babel transforms this file), NOT plain `npx jest`.
 */

const UOM_KEYS = [
  "dual_uom",
  "procurement_uom",
  "consumption_uom",
  "conversion_factor",
];

/**
 * @param {object} tagForm    current edit-dialog form state
 * @param {object} editingRM  the RM as it exists in the DB right now
 * @returns {object}          payload to PUT to /raw-materials/{rm_id}
 */
function buildRmEditPayload(tagForm, editingRM) {
  // Start from every non-UOM key on the form (brands, verticals,
  // models, category, uom, source_type, is_brand_specific, …). This
  // preserves every existing tagging edit unchanged.
  const payload = {};
  for (const [k, v] of Object.entries(tagForm || {})) {
    if (!UOM_KEYS.includes(k)) {
      payload[k] = v;
    }
  }

  const formDual = Boolean(tagForm && tagForm.dual_uom);
  const rmDual = Boolean(editingRM && editingRM.dual_uom);

  if (formDual) {
    // Case C on the backend — user wants dual on. Send the whole triple
    // even when a value is blank so the backend can null a stale field.
    payload.dual_uom = true;
    payload.procurement_uom = tagForm.procurement_uom || "";
    payload.consumption_uom = tagForm.consumption_uom || "";
    payload.conversion_factor = tagForm.conversion_factor;
  } else if (rmDual) {
    // Case A on the backend — user explicitly turned dual OFF from
    // the ON state. `dual_uom: false` alone is the signal; backend
    // force-nulls the triple. Not sending the triple keeps the intent
    // unambiguous.
    payload.dual_uom = false;
  }
  // else: user never touched dual, RM wasn't dual → OMIT all four
  //       keys. Backend leaves the row's UOM fields alone.

  return payload;
}

/**
 * Sep 2026 — dual-UOM selection guard (PM_00241–243 incident).
 *
 * The dialogs' UOM Selects used to render `value={x || "KG"}` /
 * `value={x || "PCS"}`, so an untouched dropdown LOOKED selected while
 * the underlying state stayed "". Saving then shipped empty strings,
 * which `update_raw_material` Case C (`rm_routes.py:5664-5667`)
 * coerces with `or None` — dual_uom + conversion_factor saved, both
 * UOMs silently nulled. This guard is the save-time block: when dual
 * is on, BOTH UOM fields must hold an explicit, non-blank selection.
 *
 * @param {object} form  { dual_uom, procurement_uom, consumption_uom }
 * @returns {string|null}  error message when the save must be blocked,
 *                         null when the shape is saveable.
 */
function validateDualUomSelection(form) {
  if (!form || !form.dual_uom) return null;
  const missing = [];
  if (!form.procurement_uom || !String(form.procurement_uom).trim()) {
    missing.push("Procurement UOM");
  }
  if (!form.consumption_uom || !String(form.consumption_uom).trim()) {
    missing.push("Consumption UOM");
  }
  if (missing.length === 0) return null;
  return `Select ${missing.join(" and ")} to use Dual UOM — the dropdowns apply no default, and saving without a selection would clear those fields.`;
}

export { buildRmEditPayload, validateDualUomSelection, UOM_KEYS };
